import ApplicationController from './application_controller';

// Manages the visit note's care_plan_id from the right-column "Plans of care"
// widget. Three top-of-widget states are toggled via targets:
//   - boundSection         → "Currently bound: <label> ×"
//   - previewActionSection → "Select this plan of care" button
//   - noBoundSection       → empty-state hint
// The picker (autocomplete + preview area) is hidden once a POC is bound, so
// therapists must click × to clear before binding a different POC.
//
// Required values:
//   data-care-plan-binding-reload-url-value="/visit-notes/skilled-interventions"
// Optional values:
//   data-care-plan-binding-model-id-value="<%= visit_note.id %>"
//   data-care-plan-binding-crud-action-value="new|edit"
export default class extends ApplicationController {
  static targets = [
    'boundSection',
    'boundLabel',
    'previewActionSection',
    'selectButton',
    'selectButtonLabel',
    'disciplineMismatch',
    'noBoundSection',
    'pickerSection',
    'previewArea',
  ];

  static values = {
    reloadUrl: String,
    modelId: String,
    crudAction: String,
    // The document's discipline. A POC of a different discipline can be previewed but not bound.
    documentDiscipline: String,
  };

  // Caught from awc-autocomplete:commit — captures the picked POC id+label and
  // surfaces the top-of-widget Select button. A different-discipline POC is allowed to preview
  // (sidebar-picker#select still renders it below) but cannot be bound, so we swap the bind
  // button for an explanatory notice instead.
  preview(event) {
    const { value: id, target, text } = event.detail || {};
    if (!id) return;

    const disciplineId = target?.dataset?.disciplineId || '';
    if (
      this.hasDocumentDisciplineValue &&
      this.documentDisciplineValue &&
      disciplineId &&
      disciplineId !== this.documentDisciplineValue
    ) {
      this.#showDisciplineMismatch();
      return;
    }

    if (!this.hasSelectButtonTarget) return;

    const label = (text || '').trim();
    this.selectButtonTarget.dataset.carePlanId = id;
    // Keep the picked POC's label in the dataset — the bound banner ("Currently bound: …")
    // still reads it on select(). The button itself stays a fixed call-to-action: the user
    // can already see the dates/active status in the picker, so we don't repeat them here.
    this.selectButtonTarget.dataset.carePlanLabel = label;
    if (this.hasSelectButtonLabelTarget) {
      this.selectButtonLabelTarget.textContent = 'Bind to visit note';
    }

    this.#showPreviewAction();
  }

  select(event) {
    const button = event.currentTarget;
    const carePlanId = button?.dataset?.carePlanId;
    const label = button?.dataset?.carePlanLabel || '';
    if (!carePlanId) return;

    this.#setHiddenField(carePlanId);
    this.#showBound(label);
    this.#reloadSkilledInterventions(carePlanId);
  }

  clear(event) {
    event?.preventDefault();
    this.#setHiddenField('');
    this.#showUnbound();
    this.#reloadSkilledInterventions('');
  }

  // Fires from `auto-save:loaded@window` after the auto-save controller restores form state.
  // The data-nested wrapper inside this widget rebuilds its own HTML (bound section visibility,
  // labels, preview content), and the skilled-interventions data-nested container does the same.
  // What autosave does NOT restore is the `reference-widget--bound` class on this <section>
  // (it's an attribute on the controller's host element, not inside the dump), so derive it
  // from the restored DOM state here.
  restored() {
    const bound = this.hasBoundSectionTarget && !this.boundSectionTarget.hidden;
    this.element.classList.toggle('reference-widget--bound', bound);
  }

  // The hidden field lives on the form (added in _custom_form_new/_edit.html.erb)
  // and carries the `care-plan-binding-target="carePlanIdField"` attribute. We
  // locate it by attribute rather than via this controller's targets so the
  // widget partial can sit anywhere in the DOM relative to the form.
  #setHiddenField(value) {
    const field = document.querySelector('[data-care-plan-binding-target="carePlanIdField"]');
    if (field) field.value = value;
  }

  #showBound(label) {
    if (this.hasBoundLabelTarget) this.boundLabelTarget.textContent = label;
    if (this.hasBoundSectionTarget) this.boundSectionTarget.hidden = false;
    if (this.hasPreviewActionSectionTarget) this.previewActionSectionTarget.hidden = true;
    if (this.hasDisciplineMismatchTarget) this.disciplineMismatchTarget.hidden = true;
    if (this.hasNoBoundSectionTarget) this.noBoundSectionTarget.hidden = true;
    if (this.hasPickerSectionTarget) this.pickerSectionTarget.hidden = true;
    this.element.classList.add('reference-widget--bound');
  }

  #showPreviewAction() {
    if (this.hasBoundSectionTarget) this.boundSectionTarget.hidden = true;
    if (this.hasPreviewActionSectionTarget) this.previewActionSectionTarget.hidden = false;
    if (this.hasDisciplineMismatchTarget) this.disciplineMismatchTarget.hidden = true;
    if (this.hasNoBoundSectionTarget) this.noBoundSectionTarget.hidden = true;
    // Picker stays visible while previewing so user can preview a different POC.
  }

  // Different-discipline POC was previewed: keep the preview, but show a "can't bind" notice
  // in place of the bind button. Picker stays visible so they can pick a same-discipline POC.
  #showDisciplineMismatch() {
    if (this.hasBoundSectionTarget) this.boundSectionTarget.hidden = true;
    if (this.hasPreviewActionSectionTarget) this.previewActionSectionTarget.hidden = true;
    if (this.hasDisciplineMismatchTarget) this.disciplineMismatchTarget.hidden = false;
    if (this.hasNoBoundSectionTarget) this.noBoundSectionTarget.hidden = true;
  }

  #showUnbound() {
    if (this.hasBoundLabelTarget) this.boundLabelTarget.textContent = '';
    if (this.hasBoundSectionTarget) this.boundSectionTarget.hidden = true;
    if (this.hasPreviewActionSectionTarget) this.previewActionSectionTarget.hidden = true;
    if (this.hasDisciplineMismatchTarget) this.disciplineMismatchTarget.hidden = true;
    if (this.hasNoBoundSectionTarget) this.noBoundSectionTarget.hidden = false;
    if (this.hasPickerSectionTarget) this.pickerSectionTarget.hidden = false;
    // Drop the previously-bound POC's preview content so the picker starts fresh.
    if (this.hasPreviewAreaTarget) this.previewAreaTarget.innerHTML = '';
    this.element.classList.remove('reference-widget--bound');
  }

  async #reloadSkilledInterventions(carePlanId) {
    const container = document.getElementById('skilled-interventions-container');
    if (!container || !this.reloadUrlValue) return;

    const url = new URL(this.reloadUrlValue, window.location.origin);
    if (carePlanId) url.searchParams.set('care_plan_id', carePlanId);
    if (this.crudActionValue) url.searchParams.set('crud_action', this.crudActionValue);
    if (this.modelIdValue) url.searchParams.set('model_id', this.modelIdValue);

    container.innerHTML =
      '<div class="text-center p-3"><div class="spinner-border spinner-border-sm" role="status"><span class="sr-only">Loading...</span></div></div>';

    try {
      const res = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const text = await res.text();
      // The endpoint response wraps the checkboxes in its own <div id="skilled-interventions-container">.
      // Strip the wrapper so we don't accumulate nested containers on repeated POC switches.
      const wrapper = document.createElement('div');
      wrapper.innerHTML = text;
      const inner = wrapper.querySelector('#skilled-interventions-container');
      // Strip the legacy missing-SIs warning alert — the sidebar surfaces its own simple empty-state
      // copy instead of the multi-link card that would otherwise crowd the widget body.
      inner?.querySelectorAll('.alert').forEach((el) => el.remove());
      const html = (inner ? inner.innerHTML : text).trim();
      // The server response already carries the correct empty-state copy (see
      // visit_notes/skilled_interventions + shared _skilled_interventions_empty_state), so this
      // fallback only fires if the response is unexpectedly empty. Branch on whether a POC is
      // bound so the copy still matches: clearing a POC points the user back at the picker,
      // while a bound-but-empty POC reads as "this POC has none". Keep this text in sync with
      // the shared empty-state partial.
      const emptyState = carePlanId
        ? '<p class="reference-widget__empty"><i class="bi bi-info-circle mr-1" aria-hidden="true"></i>The selected plan of care has no skilled interventions for this patient and discipline.</p>'
        : '<p class="reference-widget__empty"><i class="bi bi-info-circle mr-1" aria-hidden="true"></i>Select a plan of care in the <strong>Plans of care</strong> section above to see its skilled interventions here.</p>';
      container.innerHTML = html || emptyState;
    } catch {
      container.innerHTML =
        '<p class="reference-widget__empty"><i class="bi bi-info-circle mr-1" aria-hidden="true"></i>Couldn\'t reload skilled interventions.</p>';
    }
  }
}
